<template>
    <div class="menu-wrapper">
        <div class="menu-container" ref="menu-container">
            <span class="menu-underline" :style="underlineStyle"></span>
            <button
                v-for="(item, i) in items" :key="i" :ref="'item-' + i"
                :class="['menu-item', {selected: isSelected(i)}]"
                :dusk="'menu-item-' + i"
                @click="selectItem(i)"
            >
                {{ item }}
            </button>
        </div>
        <button v-if="showScrollButton" class="scroll-button show-for-large" @click="scrollRight">
            <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0.599804 0.499903C0.199804 0.899902 0.199804 1.4999 0.599804 1.8999L4.4998 5.7999L0.599805 9.6999C0.199805 10.0999 0.199805 10.6999 0.599805 11.0999C0.999805 11.4999 1.5998 11.4999 1.9998 11.0999L6.5998 6.4999C6.9998 6.0999 6.9998 5.4999 6.5998 5.0999L1.9998 0.499903C1.5998 0.099903 0.999804 0.0999031 0.599804 0.499903Z" fill="#424242"/>
            </svg>
        </button>
    </div>
</template>
<script lang="ts">
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';

@Component
export default class SlidingMenu extends Vue
{
    @Prop({type: Array, required: true}) readonly items!: string[];
    @Prop({type: Number, required: true}) readonly selectedItem!: number;
    showScrollButton: boolean = false;

    mounted() {
        // Unfortunate hack to force a refresh of the underlineStyle property
        this.selectItem(0);
        this.$watch('selectedItem', this.onItemUpdate.bind(this));
        const container = this.$refs['menu-container'] as HTMLElement;
        container.addEventListener('scroll', this.checkScrollPosition);
        this.checkScrollPosition();
    }

    beforeDestroy() {
        const container = this.$refs['menu-container'] as HTMLElement;
        container.removeEventListener('scroll', this.checkScrollPosition);
    }

    checkScrollPosition() {
        const container = this.$refs['menu-container'] as HTMLElement;
        const isAtEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth;
        this.showScrollButton = !isAtEnd;
    }

    scrollRight() {
        const container = this.$refs['menu-container'] as HTMLElement;
        container.scrollBy({ left: 200, behavior: 'smooth' });
    }

    selectItem(index: number) {
        this.$emit('select', index);
    }

    onItemUpdate(newItem: number) {
        if (newItem < this.items.length) {
            const menuContainerRef = <HTMLElement>this.$refs['menu-container'];
            const selectedItemRef = (<HTMLElement[]>this.$refs[`item-${newItem}`])[0];
            if (!selectedItemRef) return;

            const itemRight = selectedItemRef.offsetLeft + selectedItemRef.getBoundingClientRect().width;
            menuContainerRef.scrollTo({
                left: (selectedItemRef.offsetLeft + itemRight) / 2 - menuContainerRef.getBoundingClientRect().width / 2,
                behavior: 'smooth'
            });
        }
    }

    get isSelected() {
        return (index: number) => {
            return index == this.selectedItem;
        };
    }

    get underlineStyle() {
        if (this.selectedItem >= 0 && this.$refs[`item-${this.selectedItem}`] !== undefined) {
            const refs = this.$refs[`item-${this.selectedItem}`] as HTMLElement[];
            if (refs && refs.length > 0 && refs[0]) {
                return {
                    left: refs[0].offsetLeft + 'px',
                    width: refs[0].getBoundingClientRect().width + 'px'
                };
            }
        }
        return {};
    }
}
</script>
<style lang="scss" scoped>
@import '~foundation-sites/scss/foundation';

.menu-container {
    padding-right: 40px;
    -webkit-overflow-scrolling: auto; //momentum-based scrolling, check if it works since it's a non-standard feature..check if needed
    scrollbar-width: none; // hide scrollbar on Firefox
    overflow: -moz-scrollbars-none; // hide scrollbar on Firefox
    -ms-overflow-style: none; // hide scrollbar on IE
    overflow-x: scroll;
    position: relative;
    &::-webkit-scrollbar {
        width: 0 !important; // hide scrollbar on Chrome and Safari
        height: 0 !important;
    }
    position: relative;
    width: 100%;
    overflow-x: auto;
    display: flex;

    .menu-item {
        display: flex;
        padding: 15px;
        border: none;
        outline: none;
        background: none;
        text-align: center;
        cursor: pointer;
        border-radius: 0;
        white-space: nowrap;
        font-weight: 400;
        color: var(--sliding-menu-color);

        &.selected {
            font-weight: 600;
            color: var(--sliding-menu-selected-color);
        }

        @include breakpoint(medium down) {
            color: var(--sliding-menu-mobile-color);

            &.selected {
                color: var(--sliding-menu-mobile-selected-color);
            }
        }
    }

    .menu-underline {
        position: absolute;
        bottom: 0;
        left: 0;
        height: 3px;
        background: var(--sliding-menu-underline-color);
        margin: 0;
        transition: all .4s ease;
        z-index: 1;
        border-radius: var(--sliding-menu-underline-radius);

        @include breakpoint(medium down) {
            background: var(--sliding-menu-mobile-underline-color);
        }
    }
}
.menu-wrapper {
    position: relative;
    width: 100%;
    overflow: hidden;
    &::after {
        display: none;
        position: absolute;
        top: 0;
        bottom: 0;
        right: 0;
        content: '';
        width: 100px;
        @include breakpoint(large) {
            display: block;
        }
        background: linear-gradient(270deg, #FFF 40.1%, rgba(255, 255, 255, 0.00) 100%);
    }
}

.scroll-button {
    z-index: 999;
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 40px;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
}

.scroll-icon {
    font-weight: bold;
    color: var(--sliding-menu-color);
}
</style>
